import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRegister } from '@/hooks';
import { Button, Input } from '@/components/ui';
import { LOGO_FULL } from '@/lib/brand';

type RegisterFormData = {
  email: string;
  password: string;
  displayName?: string;
};

export function RegisterPage(): JSX.Element {
  const { t } = useTranslation('auth');
  const registerMutation = useRegister();

  const createSchema = () =>
    z.object({
      email: z.string().email(t('validation.emailInvalid')),
      password: z.string().min(8, t('validation.passwordMinLengthDefault')),
      displayName: z.string().optional(),
    });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(createSchema()),
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      await registerMutation.mutateAsync(data);
    } catch {
      // Error is handled by mutation
    }
  };

  const errorMessage = registerMutation.error ? registerMutation.error.message : null;
  const isBusy = isSubmitting || registerMutation.isPending;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center gap-4">
          <img
            src={LOGO_FULL}
            alt="StoneHartDen"
            className="h-14 w-auto object-contain sm:h-16"
          />
          <h2 className="text-center font-heading text-heading-xl text-brand-950">
            {t('register.title')}
          </h2>
        </div>
        <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
          <Input
            {...register('email')}
            id="email"
            type="email"
            autoComplete="email"
            label={t('register.email')}
            placeholder={t('register.email')}
            error={errors.email?.message}
          />
          <Input
            {...register('password')}
            id="password"
            type="password"
            autoComplete="new-password"
            label={t('register.password')}
            placeholder={t('register.password')}
            error={errors.password?.message}
          />
          <Input
            {...register('displayName')}
            id="displayName"
            type="text"
            autoComplete="name"
            label={t('register.displayName')}
            placeholder={t('register.displayName')}
            error={errors.displayName?.message}
          />

          {errorMessage ? (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{errorMessage}</p>
            </div>
          ) : null}

          <Button type="submit" fullWidth loading={isBusy} disabled={isBusy}>
            {isBusy ? t('app.loading', { ns: 'common' }) : t('register.submit')}
          </Button>

          <div className="text-center">
            <Link
              to="/login"
              className="font-medium text-brand-600 transition-colors hover:text-brand-400"
            >
              {t('register.loginLink')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
